/**
 * Параметры протокола. Значения по умолчанию взяты из docs/04-onchain-architecture.md
 * («Токеномика запуска») и ADR-0007. В контрактах эти же числа живут в конфиге
 * фабрики и фиксируются в состоянии кривой на момент запуска, поэтому здесь они
 * тоже параметры, а не константы, разбросанные по формулам.
 */

import { toNano as coreToNano } from '@ton/core';

export const NANO = 1_000_000_000n;
export const BPS_DENOM = 10_000n;
export const JETTON_DECIMALS = 9;

/** TON → нанотоны. */
export const toNano = coreToNano;

/** Жетоны (9 знаков) → наножетоны: та же размерность, что у TON. */
export const toJettons = coreToNano;

export interface CurveConfig {
  /** Общая эмиссия, наножетоны. */
  totalSupply: bigint;
  /** Продаётся на кривой, наножетоны. */
  curveSupply: bigint;
  /** Виртуальный TON-резерв `V0`, нанотоны. */
  virtualTon: bigint;
  /** Виртуальный жетон-резерв `J0`, наножетоны. */
  virtualJettons: bigint;
  /** Порог градуации по `real_ton`, нанотоны. */
  graduationThreshold: bigint;
  /** Комиссия кривой на TON-ноге, bps. */
  tradeFeeBps: bigint;
  /** Разовая доля протокола при градуации, bps от публичных TON. */
  graduationFeeBps: bigint;
  /** Комиссия роутера после градуации на TON-ноге, bps (этап 6). */
  routerFeeBps: bigint;
  /** Ставка антиснайп-комиссии в момент открытия торговли, bps. */
  antiSnipeMaxBps: bigint;
  /** Длительность окна антиснайп-комиссии, секунды. */
  antiSnipeWindowSeconds: number;
  /** Лимит dev-buy, bps от общей эмиссии. */
  devBuyMaxBps: bigint;
  /** Плата за запуск, нанотоны. */
  launchFee: bigint;
  /** Сплит торговой комиссии кривой: канал / протокол / реферер, bps. */
  feeSplit: FeeSplitBps;
  /** Сплит комиссии роутера: канал / протокол / реферер, bps. */
  routerSplit: FeeSplitBps;
  /** Сплит TON-ноги harvest: канал / протокол; `referrerBps` всегда 0. */
  harvestSplit: FeeSplitBps;
}

export interface FeeSplitBps {
  channelBps: bigint;
  protocolBps: bigint;
  referrerBps: bigint;
}

export const DEFAULT_FEE_SPLIT: FeeSplitBps = {
  channelBps: 5000n,
  protocolBps: 4000n,
  referrerBps: 1000n,
};

export const DEFAULT_HARVEST_SPLIT: FeeSplitBps = {
  channelBps: 5000n,
  protocolBps: 5000n,
  referrerBps: 0n,
};

export const DEFAULT_ROUTER_FEE_BPS = 50n;

export const DEFAULT_CURVE_CONFIG: CurveConfig = {
  totalSupply: 1_000_000_000n * NANO,
  curveSupply: 800_000_000n * NANO,
  virtualTon: 300n * NANO,
  virtualJettons: 1_040_000_000n * NANO,
  graduationThreshold: 1000n * NANO,
  tradeFeeBps: 100n,
  graduationFeeBps: 500n,
  routerFeeBps: DEFAULT_ROUTER_FEE_BPS,
  antiSnipeMaxBps: 9900n,
  antiSnipeWindowSeconds: 5,
  devBuyMaxBps: 200n,
  launchFee: 1n * NANO,
  feeSplit: DEFAULT_FEE_SPLIT,
  routerSplit: DEFAULT_FEE_SPLIT,
  harvestSplit: DEFAULT_HARVEST_SPLIT,
};

/** Резерв под пул: эмиссия минус продаваемая на кривой часть. */
export function poolReserve(config: CurveConfig): bigint {
  return config.totalSupply - config.curveSupply;
}

/** Лимит dev-buy в наножетонах. */
export function devBuyLimit(config: CurveConfig): bigint {
  return (config.totalSupply * config.devBuyMaxBps) / BPS_DENOM;
}

function assertFeeSplit(name: string, split: FeeSplitBps, referrerMustBeZero: boolean): void {
  const { channelBps, protocolBps, referrerBps } = split;
  if (channelBps + protocolBps + referrerBps !== BPS_DENOM) {
    throw new Error(`config: ${name} must sum to 10000 bps`);
  }
  if (referrerMustBeZero && referrerBps !== 0n) {
    throw new Error(`config: ${name} referrerBps must be 0`);
  }
}

export function assertConfig(config: CurveConfig): void {
  if (config.curveSupply >= config.virtualJettons) {
    throw new Error('config: curveSupply must be less than virtualJettons, otherwise Y hits zero');
  }
  if (config.virtualTon <= 0n || config.virtualJettons <= 0n) {
    throw new Error('config: virtual reserves must be positive');
  }
  if (config.antiSnipeWindowSeconds <= 0) {
    throw new Error('config: antiSnipeWindowSeconds must be positive');
  }
  assertFeeSplit('curve fee split', config.feeSplit, false);
  assertFeeSplit('router fee split', config.routerSplit, false);
  assertFeeSplit('harvest fee split', config.harvestSplit, true);
}

/**
 * Порядок полей совпадает с `CurveParams` в Tact и геттером `params()` кривой.
 * Используется Mini App и тестами на паритет с контрактом.
 */
export const CURVE_PARAMS_FIELD_COUNT = 20;

export function readCurveParamsFromStack(read: () => bigint, fieldCount = CURVE_PARAMS_FIELD_COUNT): CurveConfig {
  if (fieldCount !== 13 && fieldCount !== CURVE_PARAMS_FIELD_COUNT) throw new Error('Unsupported curve params layout');
  const totalSupply = read();
  const curveSupply = read();
  const virtualTon = read();
  const virtualJettons = read();
  const graduationThreshold = read();
  const tradeFeeBps = read();
  const graduationFeeBps = read();
  const antiSnipeMaxBps = read();
  const antiSnipeWindowSeconds = Number(read());
  const devBuyMaxBps = read();
  const channelBps = read();
  const protocolBps = read();
  const referrerBps = read();
  // Legacy testnet curves expose only the 13 bonding-curve fields; router/harvest are not deployed there.
  const routerFeeBps = fieldCount === 13 ? DEFAULT_CURVE_CONFIG.routerFeeBps : read();
  const routerChannelBps = fieldCount === 13 ? DEFAULT_CURVE_CONFIG.routerSplit.channelBps : read();
  const routerProtocolBps = fieldCount === 13 ? DEFAULT_CURVE_CONFIG.routerSplit.protocolBps : read();
  const routerReferrerBps = fieldCount === 13 ? DEFAULT_CURVE_CONFIG.routerSplit.referrerBps : read();
  const harvestChannelBps = fieldCount === 13 ? DEFAULT_CURVE_CONFIG.harvestSplit.channelBps : read();
  const harvestProtocolBps = fieldCount === 13 ? DEFAULT_CURVE_CONFIG.harvestSplit.protocolBps : read();
  const harvestReferrerBps = fieldCount === 13 ? DEFAULT_CURVE_CONFIG.harvestSplit.referrerBps : read();

  const config: CurveConfig = {
    totalSupply,
    curveSupply,
    virtualTon,
    virtualJettons,
    graduationThreshold,
    tradeFeeBps,
    graduationFeeBps,
    routerFeeBps,
    antiSnipeMaxBps,
    antiSnipeWindowSeconds,
    devBuyMaxBps,
    launchFee: DEFAULT_CURVE_CONFIG.launchFee,
    feeSplit: { channelBps, protocolBps, referrerBps },
    routerSplit: { channelBps: routerChannelBps, protocolBps: routerProtocolBps, referrerBps: routerReferrerBps },
    harvestSplit: { channelBps: harvestChannelBps, protocolBps: harvestProtocolBps, referrerBps: harvestReferrerBps },
  };
  assertConfig(config);
  return config;
}
